import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { translations, LOCALES, DEFAULT_LOCALE } from "./translations";

const I18nContext = createContext(null);

/**
 * Resolve a dot-separated translation key ("admin.churchManagement") within a
 * single language's catalog. Falls back through these layers in order:
 *   1. Exact key in the current language
 *   2. Pluralised key (`{key}_one` / `{key}_other` based on `count`)
 *   3. Same key in the DEFAULT_LOCALE (English)
 *   4. The key itself (developers see the raw key — clear broken-translation signal)
 */
function resolve(cat, key) {
  if (!cat) return undefined;
  const parts = key.split(".");
  let node = cat;
  for (const p of parts) {
    if (node == null || typeof node !== "object") return undefined;
    node = node[p];
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(template, vars) {
  if (!template || !vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => (vars[name] ?? `{{${name}}}`));
}

function pickPlural(key, count) {
  // English-style: 1 → "one", everything else → "other". Extend per-language if needed later.
  if (count === 1) return `${key}_one`;
  return `${key}_other`;
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const stored = localStorage.getItem("locale");
      if (stored && translations[stored]) return stored;
    } catch {
      /* SSR / disabled storage */
    }
    // Attempt browser hint
    const nav = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : null;
    if (nav && translations[nav]) return nav;
    return DEFAULT_LOCALE;
  });

  const setLocale = useCallback((next) => {
    if (!translations[next]) return;
    setLocaleState(next);
    try { localStorage.setItem("locale", next); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Keep <html lang=".."> in sync so screen-readers + browser translate UIs behave.
    if (typeof document !== "undefined") document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key, vars) => {
      const cat = translations[locale];
      let template;
      // Pluralisation: if a `count` prop is provided, prefer the _one/_other variant.
      if (vars && typeof vars.count === "number") {
        template = resolve(cat, pickPlural(key, vars.count));
      }
      if (template == null) template = resolve(cat, key);
      if (template == null && locale !== DEFAULT_LOCALE) {
        // Fall back to English
        const fallback = translations[DEFAULT_LOCALE];
        if (vars && typeof vars.count === "number") {
          template = resolve(fallback, pickPlural(key, vars.count));
        }
        if (template == null) template = resolve(fallback, key);
      }
      if (template == null) return key; // Final fallback — surface the raw key
      return interpolate(template, vars);
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t, locales: LOCALES }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useTranslation must be used inside <I18nProvider>");
  return ctx;
}
